import { useMemo, useState } from "react"
import { useRouterState } from "@tanstack/react-router"
import { useConfig } from "@/features/config"
import { env } from "@/config/env"
import { usePlans } from "./use-plans"
import { defaultPlan, getDefaultPlan } from "../config"
import {
  buildRequestedPlanIds,
  resolveVisiblePlans,
  resolveSelectedPlan,
  type PlanParams,
} from "@/features/products"
import type { ProductPlan } from "../types"

/** Last-resort list when no plans backend is configured at all. */
const staticPlans = [defaultPlan]

export interface PlanSelectionResult {
  visiblePlans: ProductPlan[]
  selectedPlan: ProductPlan | null
  setSelectedPlan: (plan: ProductPlan) => void
  isLoading: boolean
}

/**
 * Resolves which plans to show on checkout and which is pre-selected.
 *
 * - `plan`/`plans` from the URL or funnel config → fetch those.
 * - Nothing specified → fetch the configured default plan (VITE_DEFAULT_PLAN_ID),
 *   so price and copy come from real plan data instead of a placeholder.
 * - No plans backend configured at all → static fallback from config.
 *
 * Selection is kept as an id and the plan object is derived during render, so
 * the pre-selected default updates naturally when async plans arrive — no effects.
 */
export function usePlanSelection(): PlanSelectionResult {
  const search = useRouterState({ select: (s) => s.location.search }) as Record<string, unknown>
  const { params: configParams } = useConfig()

  // Live URL params take precedence over the funnel config.
  const planParam = (typeof search.plan === "string" ? search.plan : undefined) ?? configParams.plan
  const plansParam =
    (typeof search.plans === "string" ? search.plans : undefined) ?? configParams.plans

  const params: PlanParams = useMemo(
    () => ({ plan: planParam, plans: plansParam }),
    [planParam, plansParam],
  )

  const paramIds = useMemo(() => buildRequestedPlanIds(params), [params])
  const defaultPlanId = env.VITE_DEFAULT_PLAN_ID.trim()

  // Fall back to the configured default plan id when the funnel didn't name one.
  const requestedIds = useMemo(
    () => (paramIds.length > 0 ? paramIds : defaultPlanId ? [defaultPlanId] : []),
    [paramIds, defaultPlanId],
  )

  const { plans: dynamicPlans, status, isBackendConfigured } = usePlans(requestedIds)

  const useDynamic = isBackendConfigured && requestedIds.length > 0

  const visiblePlans = useMemo(
    () => (useDynamic ? resolveVisiblePlans(params, dynamicPlans) : staticPlans),
    [useDynamic, params, dynamicPlans],
  )

  const defaultSelected = useMemo(
    () => (useDynamic ? resolveSelectedPlan(params, visiblePlans) : getDefaultPlan()),
    [useDynamic, params, visiblePlans],
  )

  const [selectedId, setSelectedId] = useState<string | null>(null)

  const selectedPlan =
    visiblePlans.find((plan) => plan.id === selectedId) ?? defaultSelected ?? null

  const isLoading =
    useDynamic && visiblePlans.length === 0 && (status === "loading" || status === "idle")

  return {
    visiblePlans,
    selectedPlan,
    setSelectedPlan: (plan) => setSelectedId(plan.id),
    isLoading,
  }
}
